import { useEffect, useState } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { api, parseErr } from '@/lib/api';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { ClipboardList, Lock, Calendar, Rocket, Hourglass, MessageSquare } from 'lucide-react';

const VERSION_META = {
  v01: { label: 'Version 01 — Foundation', Icon: Rocket },
  v02: { label: 'Version 02 — Insight & Reporting', Icon: Hourglass },
  v03: { label: 'Version 03 — Reserved for team feedback', Icon: MessageSquare },
};

export default function ReleaseManagerPage() {
  const [data, setData] = useState(null);
  const [plan, setPlan] = useState(null);
  const [saving, setSaving] = useState('');

  const load = async () => {
    try {
      const [f, p] = await Promise.all([api.get('/admin/features'), api.get('/releases/plan')]);
      setData(f.data);
      setPlan(p.data);
    } catch (e) { toast.error(parseErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (feat, role) => {
    setSaving(feat.feature);
    const current = new Set(feat.enabled_roles || []);
    current.has(role) ? current.delete(role) : current.add(role);
    try {
      await api.put(`/admin/features/${feat.feature}`, { enabled_roles: Array.from(current) });
      load();
    } catch (e) { toast.error(parseErr(e)); }
    finally { setSaving(''); }
  };

  const setSchedule = async (feat, iso) => {
    try {
      await api.put(`/admin/features/${feat.feature}`, { scheduled_release_at: iso || null });
      toast.success('Schedule saved'); load();
    } catch (e) { toast.error(parseErr(e)); }
  };

  if (!data) return <AppShell><div className="p-8">Loading…</div></AppShell>;
  const versions = ['v01', 'v02', 'v03'];
  const alwaysOn = new Set(data.always_on_roles || []);
  const planByVersion = Object.fromEntries((plan?.releases || []).map(r => [r.version, r]));

  return (
    <AppShell breadcrumbs={[{ label: 'Admin', to: '/admin/users' }, { label: 'Release Manager' }]}>
      <div className="p-6 max-w-[1400px] mx-auto fade-up" data-testid="release-manager">
        <h1 className="font-heading text-2xl font-semibold flex items-center gap-2"><ClipboardList size={22} />Release Manager</h1>
        <p className="text-[13px] text-[hsl(var(--foreground-muted))] mb-4">
          The approved release plan: V01 → V02 → V03. Per-role toggles and scheduled dates below.
          <b className="ml-1">Owner and Accounts always see every feature</b> — those columns are locked on.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6" data-testid="release-plan-cards">
          {versions.map(v => {
            const meta = VERSION_META[v];
            const rel = planByVersion[v];
            const isV03 = v === 'v03';
            return (
              <div key={v} className="card p-4 elev-1" data-testid={`release-card-${v}`}>
                <div className="flex items-center gap-2">
                  <meta.Icon size={16} className="text-[hsl(var(--primary))]" />
                  <div className="font-heading font-semibold text-[14.5px]">{meta.label}</div>
                </div>
                <div className="mt-2 flex items-center gap-2">
                  {isV03 ? (
                    <span className="chip">Not planned — awaits team feedback after V02</span>
                  ) : (
                    <>
                      <span className="chip chip-info tabular-nums">
                        <Calendar size={10} className="inline mr-1" />
                        {rel?.release_date || (v === 'v01' ? '2026-08-06' : '2026-08-25')}
                      </span>
                      {rel?.released
                        ? <span className="chip chip-success">Released</span>
                        : <span className="chip chip-warning">Scheduled</span>}
                    </>
                  )}
                </div>
                {!isV03 && rel?.tagline && (
                  <div className="text-[12px] text-[hsl(var(--foreground-muted))] mt-2">{rel.tagline}</div>
                )}
                {isV03 && (
                  <div className="text-[12px] text-[hsl(var(--foreground-muted))] mt-2">{plan?.v03?.note}</div>
                )}
              </div>
            );
          })}
        </div>

        {versions.map(v => {
          const rows = data.features.filter(f => f.phase === v);
          if (rows.length === 0) return null;
          return (
            <section key={v} className="mb-6" data-testid={`phase-${v}`}>
              <div className="font-heading font-semibold mb-2">{VERSION_META[v].label}</div>
              <div className="card overflow-x-auto">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Feature</th>
                      {data.roles.map(r => <th key={r} className="text-center text-[11px] capitalize">{r.replace('_', ' ')}</th>)}
                      <th>Scheduled release</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(f => (
                      <tr key={f.feature} data-testid={`feature-row-${f.feature}`}>
                        <td>
                          <div className="text-[13px] font-medium">{f.label}</div>
                          <div className="text-[11px] text-[hsl(var(--foreground-subtle))] font-mono">{f.feature}</div>
                        </td>
                        {data.roles.map(role => {
                          const enabled = (f.enabled_roles || []).includes(role);
                          const locked = alwaysOn.has(role) && !f.strict_roles;
                          return (
                            <td key={role} className="text-center">
                              {locked ? (
                                <span className="chip chip-success inline-flex items-center gap-1" title="Always on"><Lock size={10} />ON</span>
                              ) : (
                                <button
                                  data-testid={`toggle-${f.feature}-${role}`}
                                  onClick={() => toggle(f, role)}
                                  disabled={saving === f.feature}
                                  className={`inline-block w-9 h-5 rounded-full border transition-colors relative ${enabled ? 'bg-[hsl(var(--success))]' : 'bg-[hsl(var(--surface-2))]'}`}
                                  style={{ borderColor: 'hsl(var(--border))' }}>
                                  <span className={`absolute top-0.5 ${enabled ? 'right-0.5' : 'left-0.5'} w-4 h-4 rounded-full bg-[hsl(var(--card))] shadow-sm transition-all`} />
                                </button>
                              )}
                            </td>
                          );
                        })}
                        <td>
                          <Input type="date" defaultValue={f.scheduled_release_at ? f.scheduled_release_at.slice(0, 10) : ''}
                                 onBlur={e => {
                                   const val = e.target.value;
                                   const iso = val ? new Date(val + 'T00:00:00Z').toISOString() : null;
                                   if ((iso || '') !== (f.scheduled_release_at || '')) setSchedule(f, iso);
                                 }}
                                 data-testid={`schedule-${f.feature}`} className="w-[140px]" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          );
        })}

        <div className="text-[12px] text-[hsl(var(--foreground-muted))]">
          <Calendar size={11} className="inline mr-1" />
          Scheduled features become visible automatically to their allowed roles once the date passes.
          A feature toggled off is invisible — no locked buttons, no teasers.
        </div>
      </div>
    </AppShell>
  );
}
